import UpdatedAt from '../components/UpdatedAt.jsx';
import { today } from '../lib/dateRange.js';
import { formatMinutes } from '../lib/format.js';
import { useLiveData } from '../lib/useLiveData.js';
import { useSession, ROLES } from '../lib/session.jsx';

/* Today's figures.

   Counted as the business counts them, not as the PBX logs them: one call is
   one call however many extensions it rang, external only, and a caller who
   reached voicemail was still missed. The arithmetic lives in CallStats —
   this only renders it.

   Refreshed on a timer, because a day's figures change under you: a page
   loaded at 14:00 showing "1 outbound" is wrong by 14:35, and silently so. */
export default function Overview() {
  const { role } = useSession();
  // Pin "today" to the tenant's timezone so it matches how the PBX files dates,
  // rather than the portal server's clock.
  const { status, data, error, updatedAt, refresh } = useLiveData(`/stats?date=${today()}`, { intervalMs: 60000 });

  if (status === 'loading') {
    return <div className="card"><div className="empty"><p className="empty__body">Loading today's figures…</p></div></div>;
  }

  if (status === 'error') {
    return (
      <div className="card">
        <div className="empty">
          <div className="empty__title">We couldn't load today's figures</div>
          <p className="empty__body">{error?.message}</p>
        </div>
      </div>
    );
  }

  const { inbound, outbound } = data.stats;
  const mine = role === ROLES.EXTENSION_USER;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <UpdatedAt at={updatedAt} onRefresh={refresh} stale={Boolean(error)} />
      </div>

      <div className="grid-3">
        <Stat
          label={mine ? 'Calls to you today' : 'Calls received today'}
          value={inbound.total}
          sub={`${formatMinutes(inbound.talk_seconds)} on incoming calls`}
          note={inbound.answer_rate !== null ? `${inbound.answer_rate}% answered` : null}
        />
        <Stat
          label="Missed"
          value={inbound.missed}
          tone={inbound.missed > 0 ? 'var(--miss)' : undefined}
          sub={
            inbound.voicemail > 0
              ? `${inbound.voicemail} left a voicemail`
              : 'No voicemails left'
          }
          note={
            // An extension user's misses are only the calls nobody took. The
            // ones a colleague answered are called out here so the number
            // can't be read as "you let these go".
            mine && inbound.answered_elsewhere > 0
              ? `${inbound.answered_elsewhere} more answered by a colleague`
              : inbound.missed > 0
                ? 'Nobody picked up'
                : null
          }
        />
        <Stat
          label="Outbound calls"
          value={outbound.total}
          sub={`${formatMinutes(outbound.talk_seconds)} on outbound calls`}
          note={outbound.total > 0 ? `${outbound.answered} connected` : null}
        />
      </div>

      <p className="footnote">
        Today's calls, external only — calls between your own extensions aren't counted.
        A call that rings several extensions is counted once.
      </p>
    </div>
  );
}

function Stat({ label, value, sub, note, tone }) {
  return (
    <div className="card stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={tone ? { color: tone } : undefined}>{value}</div>
      <div className="stat__sub">{sub}</div>
      {note && (
        <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 6, opacity: 0.8 }}>{note}</div>
      )}
    </div>
  );
}
